<script setup>
import cdnFile from '@/helpers/cdnFile.js'
import formatMessage from '@/helpers/formatMessage.js'
import { generateSrcSet } from '@/helpers/srcSetHelper'
import SvgIcon from '@/components/SvgIcon.vue'

const props = defineProps({
  displayModel: {
    type: Object,
    required: true,
  },
  selectedModelId: {
    type: [String, Number],
    required: false,
    default: null,
  },
  user: {
    type: Object,
    required: false,
    default: null,
  },
  canDeleteModel: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['select', 'clear', 'delete', 'togglePin'])

function onModelClick() {
  emit('select', props.displayModel)
}

function onClearModel() {
  emit('clear', props.displayModel)
}

function onDeleteModel() {
  emit('delete', props.displayModel)
}

function onTogglePin() {
  emit('togglePin', props.displayModel)
}
</script>

<template>
  <div
    class="flex gap-x-[15px] items-center bg-[#0E122A] mx-[12px] mb-[15px] py-[22px] px-[15px] rounded-[15px] border-[1px] cursor-pointer min-h-14 relative"
    :class="
      selectedModelId == displayModel.id
        ? 'border-[#B651FF] selected-chat'
        : 'border-transparent'
    "
    @click="onModelClick"
  >
    <div
      class="h-[43px] w-[43px] bg-gray-500 rounded-full overflow-hidden shrink-0"
    >
      <img
        v-if="
          displayModel.premadeId &&
          !displayModel.profilePicture?.startsWith('sd/')
        "
        class="w-full rounded-xl aspect-[832/1216] no-image-select"
        :src="generateSrcSet('models', displayModel.name, 'webp', 'xs', true)"
        :alt="displayModel.name"
        draggable="false"
      />
      <img
        v-else
        class="w-full rounded-xl aspect-[832/1216] no-image-select"
        :src="
          cdnFile(displayModel.profilePicture || displayModel.referenceImage)
        "
        :alt="displayModel.name"
        draggable="false"
      />
    </div>
    <div class="flex flex-col w-full truncate">
      <div class="flex items-center">
        <div class="">{{ displayModel.name }}</div>
        <button
          v-if="user?.subscription"
          @click.stop="onTogglePin"
          class="flex justify-center items-center bg-[#11152D] p-1.5 my-[1px] rounded-full"
        >
          <SvgIcon
            v-if="!displayModel.pinned"
            icon-id="push-pin-outline"
            class="w-3.5 h-3.5"
          />
          <SvgIcon v-else icon-id="push-pin" class="w-3.5 h-3.5" />
        </button>
      </div>
      <div
        class="text-[13px] text-[#B1B5DB] truncate max-w-full"
        v-html="
          displayModel?.messages?.[0]?.imageDescription
            ? 'Sent a picture'
            : formatMessage(displayModel?.messages?.[0]?.content)
        "
      ></div>
    </div>
    <div class="flex gap-[10px]">
      <div
        v-if="user?.subscription"
        class="rounded-full p-[7px] w-[35px] h-[35px]"
        style="box-shadow: 0px 0px 0px 1.2000000476837158px #1e1a3d"
        @click.stop="onClearModel"
      >
        <SvgIcon icon-id="broom" class="w-[21px] h-[21px]" />
      </div>
      <div
        v-if="canDeleteModel"
        class="rounded-full p-[7px] w-[35px] h-[35px]"
        :style="{
          'box-shadow':
            '0px 0px 0px 1.2000000476837158px #1E1A3D, 0px 0px 12px 2.4000000953674316px #13142E inset',
        }"
        @click.stop="onDeleteModel"
      >
        <SvgIcon icon-id="trash" class="w-[21px] h-[21px]" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.selected-chat {
  box-shadow: 0px 0px 0px 3px #b552ff26;
}
</style>
